import React, { useState, useRef, useEffect, useCallback } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles'
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import { CoreTextField, CoreButton } from 'src/components/core'
import TextFieldPopup from 'src/components/textfield-popup'
import SuggestionSkeleton from 'src/components/suggestion-skeleton'
import { aICreatorSuggestGoalAction } from 'src/reducers/ai-creator/action'
import { isMobile } from 'react-device-detect'
const TeachingGoals = ({
  classes,
  goals,
  goalsAccordionOpen,
  onGoalsAccordionToggle,
  onAddNewGoal,
  onUpdateGoal,
  onGoalKeyPress,
  onDeleteGoal, // Add this prop for deleting goals
  courseAbout, // Add this prop to get course topic
  attachments, // Add this prop to get attachments
  courseLanguageCode,
}) => {
  const dispatch = useDispatch()
  const [popupStates, setPopupStates] = useState({})
  const [anchorElements, setAnchorElements] = useState({})
  const [popupLoadingStates, setPopupLoadingStates] = useState({})
  const [fieldSuggestions, setFieldSuggestions] = useState({}) // Store suggestions per field
  const textFieldRefs = useRef({})
  const presentedSuggestionsRef = useRef({}) // Ref to access current presentedSuggestions without causing re-renders

  const handleRegenerateSuggestions = useCallback(
    fieldIndex => {
      if (!courseAbout?.trim()) {
        alert('Please enter a course topic first')
        return
      }

      // Get the previously generated suggestions for this field using ref
      const previousSuggestions =
        presentedSuggestionsRef.current[fieldIndex] || []

      const requestData = {
        topic: courseAbout,
        languageCode: courseLanguageCode,
        currentGoals: goals.filter(goal => goal.trim() !== ''),
        attachmentIds: attachments?.map(att => att.attachment_id) || [],
        presentGoals: previousSuggestions,
      }

      dispatch(
        aICreatorSuggestGoalAction(requestData, response => {
          // Extract suggestions from response - handle different response formats
          let suggestions = []
          if (response && response.suggestedGoals) {
            suggestions = response.suggestedGoals
          }

          // Update ref with new suggestions
          presentedSuggestionsRef.current = {
            ...presentedSuggestionsRef.current,
            [fieldIndex]: suggestions,
          }

          // Store suggestions for this specific field
          setFieldSuggestions(prev => ({
            ...prev,
            [fieldIndex]: suggestions,
          }))

          // Clear loading state for this specific popup
          setPopupLoadingStates(prev => ({
            ...prev,
            [fieldIndex]: false,
          }))
        }),
      )
    },
    [courseAbout, goals, attachments, courseLanguageCode, dispatch],
  )

  // When course topic changes, clear cached suggestions
  useEffect(() => {
    // Clear all cached suggestions/loading states tied to previous course topic
    setFieldSuggestions({})
    setPopupLoadingStates({})
    // Clear presentedSuggestions when topic changes to get fresh suggestions
    presentedSuggestionsRef.current = {}
  }, [courseAbout])

  // Get suggested goals from Redux store
  const { suggestionLoading: loading } = useSelector(state => state.aICreator)

  // Debug log to see current fieldSuggestions state

  // Remove the useEffect that was causing infinite loops

  const handleTextFieldFocus = index => {
    // Only show popup if the text field is empty
    if (!goals[index] || goals[index].trim() === '') {
      setPopupStates(prev => ({
        ...prev,
        [index]: true,
      }))
      setAnchorElements(prev => ({
        ...prev,
        [index]: textFieldRefs.current[index],
      }))

      // Check if we already have suggestions for this field
      if (fieldSuggestions[index]) {
        // Use existing suggestions, no need to fetch
        return
      }

      // Fetch suggestions when popup opens (if we have a course topic)
      if (courseAbout?.trim()) {
        // Set loading state for this specific popup
        setPopupLoadingStates(prev => ({
          ...prev,
          [index]: true,
        }))

        // Fetch suggestions for this specific field
        handleRegenerateSuggestions(index)
      }
    }
  }

  const handlePopupClose = index => {
    setPopupStates(prev => ({
      ...prev,
      [index]: false,
    }))
    // Clear loading state for this popup
    setPopupLoadingStates(prev => ({
      ...prev,
      [index]: false,
    }))
  }

  const handleSuggestionClick = (index, suggestion) => {
    onUpdateGoal(index, suggestion)

    // Don't clear the presentedSuggestions - keep them for regeneration
    // Only clear the fieldSuggestions for display purposes
    setFieldSuggestions(prev => ({
      ...prev,
      [index]: null,
    }))

    // Close popup after suggestion is selected
    setTimeout(() => {
      handlePopupClose(index)
    }, 50)
  }

  const handleKeyDown = (e, index) => {
    // Handle backspace key press
    if (
      e.key === 'Backspace' &&
      (!goals[index] || goals[index].trim() === '') &&
      index > 0
    ) {
      e.preventDefault()
      // Call the parent's delete function - we'll need to add this prop
      if (onDeleteGoal) {
        onDeleteGoal(index)
      }
    }
  }

  const setTextFieldRef = (index, element) => {
    if (element) {
      textFieldRefs.current[index] = element
    }
  }

  return (
    <div className={classes.courseInfo}>
      <div className={classes.courseInfoTitleBetween}>
        <span className={classes.sectionLabel}>
          What are your teaching goals?
        </span>
      </div>
      <div
        className={`${classes.accordionContent} ${
          goalsAccordionOpen ? 'open' : ''
        }`}
      >
        {goals.map((goal, index) => (
          <div key={index} className={classes.goalItem}>
            <div className={classes.goalInputContainer}>
              <div className={classes.goalInputRow}>
                <CoreTextField
                  placeholder="Enter your course goal here or select from suggested goals."
                  fullWidth
                  variant="outlined"
                  marginTop={false}
                  value={goal}
                  multiline
                  onChange={e => {
                    onUpdateGoal(index, e.target.value)
                    // Close the popup when user starts typing
                    if (popupStates[index] && e.target.value.trim() !== '') {
                      handlePopupClose(index)
                    }
                  }}
                  onKeyPress={e => onGoalKeyPress(e, index)}
                  onFocus={() => handleTextFieldFocus(index)}
                  onKeyDown={e => handleKeyDown(e, index)}
                  ref={element => setTextFieldRef(index, element)}
                  data-goal-index={index}
                />

                <div
                  className={classes.deleteButtonContainer}
                  style={{
                    visibility: goals.length === 1 ? 'hidden' : 'visible',
                  }}
                >
                  <Icon
                    icon="iconamoon:close-light"
                    style={{
                      fontSize: '20px',
                      color: '#919EAB',
                      cursor: 'pointer',
                    }}
                    onClick={() => onDeleteGoal(index)}
                  />
                </div>
              </div>

              <div className={classes.goalCharCount}>
                <Typography fontSize={12} fontWeight={400} color={'#919EAB'}>
                  {goal.length} / 250
                </Typography>
              </div>

              <TextFieldPopup
                anchorEl={anchorElements[index]}
                open={popupStates[index] || false}
                onClose={() => handlePopupClose(index)}
                placement="bottom-start"
                offset={4}
                maxHeight={250}
              >
                <div className={classes.popupContent}>
                  <div className={classes.popupTitleContainer}>
                    <Typography
                      fontSize={14}
                      fontWeight={400}
                      className={classes.popupTitle}
                    >
                      Suggested course goals:
                    </Typography>
                    {fieldSuggestions[index] &&
                      fieldSuggestions[index].length > 0 && (
                        <CoreButton
                          size="small"
                          variant="text"
                          color="secondary"
                          startIcon={
                            loading || popupLoadingStates[index] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Icon
                                icon="hugeicons:exchange-01"
                                fontSize={16}
                              />
                            )
                          }
                          onClick={() => {
                            setPopupLoadingStates(prev => ({
                              ...prev,
                              [index]: true,
                            }))
                            handleRegenerateSuggestions(index)
                          }}
                          disabled={
                            loading ||
                            popupLoadingStates[index] ||
                            !courseAbout?.trim()
                          }
                        >
                          {loading || popupLoadingStates[index]
                            ? 'Generating...'
                            : 'Regenerate'}
                        </CoreButton>
                      )}
                  </div>
                  <List dense className={classes.suggestionList}>
                    {popupLoadingStates[index] ? (
                      <SuggestionSkeleton />
                    ) : fieldSuggestions[index] &&
                      fieldSuggestions[index].length > 0 ? (
                      fieldSuggestions[index].map(
                        (suggestion, suggestionIndex) => (
                          <ListItem
                            key={suggestionIndex}
                            button
                            className={classes.suggestionItem}
                            onClick={() =>
                              handleSuggestionClick(index, suggestion)
                            }
                          >
                            <div className={classes.suggestionItemIcon}>
                              <img
                                src={'/assets/AI-Assistant-icon.svg'}
                                width={isMobile ? 16 : 24}
                                height={isMobile ? 16 : 24}
                                alt="AI Assistant"
                              />
                            </div>
                            <ListItemText
                              primary={suggestion}
                              primaryTypographyProps={{
                                fontSize: 14,
                                fontWeight: 400,
                                className: classes.suggestionText,
                              }}
                            />
                          </ListItem>
                        ),
                      )
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Typography
                          variant="body2"
                          style={{ color: '#637381' }}
                        >
                          {courseAbout?.trim()
                            ? fieldSuggestions[index] &&
                              fieldSuggestions[index].length === 0
                              ? 'No suggestions available. Try regenerating.'
                              : 'Enter a course topic to get suggestions.'
                            : 'Enter a course topic to get suggestions.'}
                        </Typography>
                      </div>
                    )}
                  </List>
                </div>
              </TextFieldPopup>
            </div>
          </div>
        ))}
        <button
          type="button"
          className={classes.addGoal}
          onClick={onAddNewGoal}
        >
          <Icon icon="tabler:plus" width={14} height={14} />
          Add another goal
        </button>
      </div>
    </div>
  )
}

export default withStyles(styles)(TeachingGoals)
